import * as React from "react";
import { useState } from "react";
import { useFormikContext } from "formik";
import { validateExternalIdRequiresArn } from "integrations/aws/utils/awsValidation";
import useAWSRegions from "archive/hooks/useAWSRegions";
import { Button, Input } from "components/bootstrap";
import { FormikFormGroup, Select } from "components/common";
const AUTH_OPTIONS = [
  { value: "automatic", label: "Automatic" },
  { value: "key_secret", label: "Key & Secret" }
];
export const validateS3CredentialsForm = (values) => {
  const errors = {};
  if (values.auth_mode === "key_secret") {
    if (!values.aws_access_key_id) {
      errors.aws_access_key_id = "Required when using Key & Secret authentication.";
    }
    if (!values.aws_secret_access_key || "delete_value" in values.aws_secret_access_key) {
      errors.aws_secret_access_key = "Required when using Key & Secret authentication.";
    }
  }
  const externalIdError = validateExternalIdRequiresArn(values.aws_assume_role_arn, values.aws_external_id);
  if (externalIdError) {
    errors.aws_external_id = externalIdError;
  }
  return errors;
};
const S3CredentialsFormFields = ({ secretIsSet }) => {
  const { values, errors, setFieldValue } = useFormikContext();
  const { data: regions } = useAWSRegions();
  const [showResetButton, setShowResetButton] = useState(secretIsSet);
  const secretError = errors.aws_secret_access_key;
  const setSecret = (secret) => setFieldValue("aws_secret_access_key", secret);
  const toggleSecretReset = () => {
    if (showResetButton) {
      setSecret({ delete_value: true });
      setShowResetButton(false);
    } else {
      setSecret({ keep_value: true });
      setShowResetButton(true);
    }
  };
  const handleAuthModeChange = (authMode) => {
    if (authMode === values.auth_mode) {
      return;
    }
    if (authMode === "automatic") {
      setFieldValue("aws_access_key_id", void 0);
      setSecret(void 0);
      setShowResetButton(false);
    } else {
      setSecret(secretIsSet ? { keep_value: true } : void 0);
      setShowResetButton(secretIsSet);
    }
    setFieldValue("auth_mode", authMode);
  };
  return /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(
    Input,
    {
      id: "credentials-auth-mode",
      label: "Authentication Type",
      labelClassName: "col-sm-3",
      wrapperClassName: "col-sm-9"
    },
    /* @__PURE__ */ React.createElement(
      Select,
      {
        id: "credentialsAuthMode",
        name: "auth_mode",
        placeholder: "Select Authentication Type",
        "aria-label": "Select authentication type",
        options: AUTH_OPTIONS,
        onChange: handleAuthModeChange,
        clearable: false,
        value: values.auth_mode
      }
    )
  ), values.auth_mode === "key_secret" && /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(
    FormikFormGroup,
    {
      name: "aws_access_key_id",
      type: "text",
      label: "AWS Access Key",
      placeholder: "AK****************",
      help: 'Your AWS Key, a 20-character alphanumeric string starting with "AK".',
      labelClassName: "col-sm-3",
      wrapperClassName: "col-sm-9",
      required: true
    }
  ), showResetButton ? /* @__PURE__ */ React.createElement(
    Input,
    {
      id: "credentials-secret-key-reset",
      label: "AWS Secret Key",
      error: secretError,
      bsStyle: secretError ? "error" : void 0,
      labelClassName: "col-sm-3",
      wrapperClassName: "col-sm-9"
    },
    /* @__PURE__ */ React.createElement(Button, { onClick: toggleSecretReset }, "Reset password")
  ) : /* @__PURE__ */ React.createElement(
    Input,
    {
      id: "credentials-secret-key",
      type: "password",
      label: "AWS Secret Key",
      error: secretError,
      bsStyle: secretError ? "error" : void 0,
      onChange: ({ target: { value } }) => setSecret(value === "" ? { delete_value: true } : { set_value: value }),
      buttonAfter: secretIsSet ? /* @__PURE__ */ React.createElement(Button, { type: "button", onClick: toggleSecretReset }, "Undo Reset") : void 0,
      placeholder: "****************",
      help: "Your AWS Secret, usually a 40-character base-64 encoded string.",
      labelClassName: "col-sm-3",
      wrapperClassName: "col-sm-9",
      required: true
    }
  )), /* @__PURE__ */ React.createElement(
    Input,
    {
      id: "credentials-aws-region",
      label: "AWS Region",
      error: errors.aws_region,
      bsStyle: errors.aws_region ? "error" : void 0,
      labelClassName: "col-sm-3",
      wrapperClassName: "col-sm-9",
      help: "Select an AWS region, or type a custom region for an S3-compatible provider (e.g. OVH, SysEleven)."
    },
    /* @__PURE__ */ React.createElement(
      Select,
      {
        id: "credentialsAwsRegion",
        name: "aws_region",
        placeholder: "Automatic",
        "aria-label": "Select AWS region",
        allowCreate: true,
        options: regions || [],
        onChange: (option) => setFieldValue("aws_region", option),
        value: values.aws_region
      }
    )
  ), /* @__PURE__ */ React.createElement(
    FormikFormGroup,
    {
      name: "endpoint_uri",
      type: "text",
      label: "S3 Endpoint URL",
      help: "Your S3 Endpoint URL. Only required to override the default endpoint.",
      labelClassName: "col-sm-3",
      wrapperClassName: "col-sm-9"
    }
  ), /* @__PURE__ */ React.createElement(
    FormikFormGroup,
    {
      name: "aws_assume_role_arn",
      type: "text",
      label: "AWS Assume Role (ARN) (optional)",
      placeholder: "arn:aws:sts::123456789012:assumed-role/some-role",
      help: "Amazon Resource Name with required cross-account permission.",
      labelClassName: "col-sm-3",
      wrapperClassName: "col-sm-9"
    }
  ), /* @__PURE__ */ React.createElement(
    FormikFormGroup,
    {
      name: "aws_external_id",
      type: "text",
      label: "AWS External ID (optional)",
      help: "External ID for the assume role, used to prevent the confused deputy problem. Only applies when an assume role ARN is provided.",
      labelClassName: "col-sm-3",
      wrapperClassName: "col-sm-9"
    }
  ));
};
export default S3CredentialsFormFields;
